import { ArrowRight, FileText, MessageSquare, Wrench } from 'lucide-react';
import { useEffect } from 'react';

import { GREETERS } from '../home/first-run';
import { PersonaAvatar } from '../persona/persona-avatar';

const POINTS = [
  {
    body: 'The kind of question you’d ask someone testing your page.',
    icon: MessageSquare,
    title: 'Ask them anything',
  },
  {
    body: 'Each persona replies in the chat.',
    icon: Wrench,
    title: 'Read their answers',
  },
  {
    body: 'When you’re done, download what they said.',
    icon: FileText,
    title: 'Get a report',
  },
] as const;

/**
 * What a room is, for somebody the extension just dropped into one. They
 * didn't come through `/new`, so nothing has said what happens here — and the
 * room is already setting itself up behind this, so reading it costs no wait.
 */
export function RoomIntroModal({ onClose }: { onClose: () => void }) {
  // Escape closes this and nothing else. The board behind leaves for home on
  // an Escape nobody took, so this one is caught first (capture phase) and
  // marked taken — otherwise, with focus off the dialog, one keypress closes
  // the intro and the room together.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/25 p-6 duration-200 animate-in fade-in">
      <div
        aria-labelledby="room-intro-title"
        aria-modal="true"
        className="w-full max-w-[440px] rounded-panel bg-surface p-7 shadow-floating duration-200 animate-in zoom-in-95 motion-reduce:animate-none"
        role="dialog"
      >
        <div aria-hidden="true" className="flex">
          {GREETERS.map((greeter, index) => (
            <PersonaAvatar
              className={index === 0 ? '' : '-ml-2'}
              key={greeter.name}
              persona={{
                accent: 0,
                avatarProfile: greeter.profile,
                id: `intro-${greeter.name}`,
                name: greeter.name,
              }}
              size="xl"
            />
          ))}
        </div>
        <h2
          className="!mt-4 !mb-0 !text-page-title !font-bold !tracking-tight !text-text-primary"
          id="room-intro-title"
        >
          Ask your AI personas
        </h2>
        <p className="mt-3 text-ui-lg leading-relaxed text-text-secondary">
          We are generating personas based on the users of your type of product.
          They&apos;ll look at your page and you can ask them what they think.
        </p>

        <ul className="mt-5 space-y-4">
          {POINTS.map(({ body, icon: Icon, title }) => (
            <li className="flex items-start gap-3" key={title}>
              <span className="flex size-9 shrink-0 items-center justify-center rounded-button bg-violet-soft text-room-violet">
                <Icon aria-hidden="true" className="size-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-ui font-semibold text-text-primary">
                  {title}
                </span>
                <span className="mt-0.5 block text-ui leading-snug text-text-secondary">
                  {body}
                </span>
              </span>
            </li>
          ))}
        </ul>

        <button
          autoFocus
          className="mt-6 flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-button bg-brand text-ui-lg font-semibold text-white transition hover:bg-brand-alt active:scale-[0.98]"
          onClick={onClose}
          type="button"
        >
          Get started
          <ArrowRight aria-hidden="true" className="size-4" />
        </button>
      </div>
    </div>
  );
}
